import { useState } from 'react';
import {
  getActivePolls,
  getPopularPolls,
  getCabinaPolls,
  getClosingSoonPolls,
  formatCount,
  getTimeRemainingText,
  pollTypeLabels,
  pollTypeIcons,
  pollTypeColors,
  pollTypeDotColors,
  pollLocationLabels,
  pollLocationIcons,
  pollDashboardStats,
  type Poll,
} from '@/mocks/rfwPolls';

function PollCard({ poll, compact }: { poll: Poll; compact?: boolean }) {
  const isActive = poll.status === 'active';
  const isClosed = poll.status === 'closed';
  const timeText = getTimeRemainingText(poll.closesAt);
  const isClosingSoon = isActive && (timeText.includes('min') || timeText.includes('h'));

  const totalVotes = poll.options.reduce((sum, o) => sum + o.votes, 0);
  const maxVotes = Math.max(...poll.options.map((o) => o.votes), 1);

  if (compact) {
    return (
      <div className="flex items-start gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-secondary-200/60 transition-all duration-200 group cursor-pointer">
        <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
          <i className={`${pollTypeIcons[poll.type]} text-sm text-secondary-600`}></i>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            {isActive && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[9px] font-bold">
                <span className={`w-1.5 h-1.5 rounded-full ${isClosingSoon ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></span>
                {isClosingSoon ? 'Cierra pronto' : 'Activa'}
              </span>
            )}
            {isClosed && (
              <span className="px-1.5 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[9px] font-medium">Cerrada</span>
            )}
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${pollTypeColors[poll.type]}`}>
              {pollTypeLabels[poll.type]}
            </span>
          </div>
          <p className="text-xs font-semibold text-foreground-900 leading-snug mb-0.5 line-clamp-2">{poll.question}</p>
          <div className="flex items-center gap-2 text-[10px] text-foreground-500">
            <span className="flex items-center gap-1">
              <i className={`${pollLocationIcons[poll.location]} text-[9px]`}></i>
              {poll.entityName}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-bar-chart-line text-[9px]"></i>
              {formatCount(totalVotes)} votos
            </span>
            {isActive && (
              <span className="flex items-center gap-1 text-amber-600">
                <i className="ri-timer-line text-[9px]"></i>
                {timeText}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-secondary-300/60 transition-all duration-300 group cursor-pointer">
      {/* Header */}
      <div className="p-4 pb-2">
        <div className="flex items-center gap-1.5 mb-2">
          {isActive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[9px] font-bold">
              <span className={`w-1.5 h-1.5 rounded-full ${isClosingSoon ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></span>
              {isClosingSoon ? 'Cierra pronto' : 'Activa'}
            </span>
          )}
          {isClosed && (
            <span className="px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[9px] font-medium">Cerrada</span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium border ${pollTypeColors[poll.type]}`}>
            {pollTypeLabels[poll.type]}
          </span>
        </div>
        <p className="text-sm font-bold text-foreground-900 leading-snug mb-2 line-clamp-2">{poll.question}</p>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="flex items-center gap-1 text-foreground-500">
            <i className={`${pollLocationIcons[poll.location]} text-[9px]`}></i>
            {poll.entityName}
          </span>
          {isActive && (
            <span className="flex items-center gap-1 text-amber-600 font-medium">
              <i className="ri-timer-line text-[9px]"></i>
              {timeText}
            </span>
          )}
        </div>
      </div>

      {/* Options with bars */}
      <div className="px-4 pb-4 space-y-2">
        {poll.options.slice(0, 4).map((opt) => {
          const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
          const barWidth = totalVotes > 0 ? Math.max((opt.votes / maxVotes) * 100, 8) : 8;
          const isWinner = poll.winnerId === opt.id;
          return (
            <div key={opt.id} className="relative">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[11px] font-medium text-foreground-800 leading-snug truncate pr-2">
                  {isWinner && <span className="text-amber-500 mr-0.5">🏆</span>}
                  {opt.text}
                </span>
                <span className="text-[10px] font-bold text-foreground-600 shrink-0">{pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isWinner ? 'bg-amber-500' : 'bg-secondary-500'}`}
                  style={{ width: `${barWidth}%` }}
                ></div>
              </div>
            </div>
          );
        })}
        {poll.options.length > 4 && (
          <p className="text-[10px] text-foreground-400 pt-1">+{poll.options.length - 4} opciones más</p>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-3 pt-2 border-t border-background-200/70 flex items-center justify-between text-[10px] text-foreground-500">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><i className="ri-bar-chart-line text-[9px]"></i>{formatCount(totalVotes)}</span>
          <span className="flex items-center gap-1"><i className="ri-group-line text-[9px]"></i>{formatCount(poll.participants)}</span>
          <span className="flex items-center gap-1"><i className="ri-chat-3-line text-[9px]"></i>{formatCount(poll.comments)}</span>
        </div>
        <span className="flex items-center gap-1 text-secondary-600 font-medium">
          <i className="ri-flashlight-line text-[9px]"></i>+{poll.score.vote} pts
        </span>
      </div>
    </div>
  );
}

export default function EncuestasSection() {
  const [activeTab, setActiveTab] = useState<'activas' | 'populares'>('activas');

  const activePolls = getActivePolls().slice(0, 6);
  const popularPolls = getPopularPolls().slice(0, 6);
  const cabinaPolls = getCabinaPolls();
  const closingSoon = getClosingSoonPolls().slice(0, 3);

  const tabs = [
    { id: 'activas' as const, label: 'Encuestas Activas', icon: 'ri-chat-poll-line', count: activePolls.length },
    { id: 'populares' as const, label: 'Más Populares', icon: 'ri-fire-line', count: popularPolls.length },
  ];

  const currentPolls = activeTab === 'activas' ? activePolls : popularPolls;

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-secondary-500 animate-pulse"></span>
            SISTEMA DE ENCUESTAS
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Tu Voz <span className="text-secondary-600">Define el Contenido</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Encuestas en Cabina, Radio, Programas, Locutores, Clubes y Eventos. Votá, compartí, comentá y sumá Score. Resultados en tiempo real.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Encuestas Activas', value: pollDashboardStats.activeNow.toString(), icon: 'ri-chat-poll-line', color: 'text-secondary-600 bg-secondary-50' },
            { label: 'Votos Totales', value: formatCount(pollDashboardStats.totalVotes), icon: 'ri-bar-chart-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Participantes', value: formatCount(pollDashboardStats.totalParticipants), icon: 'ri-group-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'En Cabina', value: pollDashboardStats.activeInCabina.toString(), icon: 'ri-chat-smile-2-line', color: 'text-red-600 bg-red-50' },
            { label: 'Cierran Pronto', value: pollDashboardStats.closingSoon.toString(), icon: 'ri-timer-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Cabina Highlight */}
        {cabinaPolls.length > 0 && (
          <div className="mb-8 p-5 rounded-2xl bg-red-50 border border-red-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-chat-smile-2-line text-sm text-red-600"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-950">Encuestas en Cabina</p>
                <p className="text-[10px] text-foreground-500">{cabinaPolls.length} activas ahora — aparecen inmediatamente para los oyentes</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {cabinaPolls.map((poll) => (
                <PollCard key={poll.id} poll={poll} compact />
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-secondary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Polls Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentPolls.map((poll) => (
            <PollCard key={poll.id} poll={poll} />
          ))}
        </div>

        {/* Closing Soon */}
        {closingSoon.length > 0 && (
          <div className="mt-8 p-5 rounded-2xl bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-timer-line text-sm text-amber-600"></i>
              </div>
              <p className="text-sm font-bold text-foreground-950">¡Cierran Pronto!</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {closingSoon.map((poll) => (
                <PollCard key={poll.id} poll={poll} compact />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href="/encuestas"
            className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 hover:bg-secondary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-chat-poll-line"></i>
            Ver Todas las Encuestas
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="text-xs text-foreground-500 mt-3">
            Descubrí, votá y participá en las encuestas de todas las radios RFW
          </p>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-secondary-100/70 border border-secondary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-chat-poll-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">Si la Cabina tiene encuestas activadas, deben aparecer inmediatamente para los oyentes.</p>
          <p className="text-xs text-foreground-600">Las encuestas no son solo para preguntar — son el pulso de tu comunidad en tiempo real.</p>
        </div>
      </div>
    </section>
  );
}